import {
  getGlofoxDocsHomeHref,
  markdocPathToSpaRoute,
} from "@/lib/portal/docs-urls";
import {
  GLOFOX_LEGACY_ACCESS_REQUEST_ROUTE,
  GLOFOX_REQUEST_ACCESS_ROUTE,
} from "@/lib/portal/glofox-routes";
import {
  PORTAL_HOME_ROUTE,
  PORTAL_SIGNUP_ROUTE,
} from "@/lib/portal/portal-routes";

export const AUTH_RETURN_PATH_STATE_KEY = "from";
export const AUTH_RETURN_PATH_QUERY_KEY = "from";

export const REQUEST_ACCESS_AUTH_NOTICE =
  "Log in or sign up to request API access.";

/** Default after auth when no `from` was provided: the Developer Portal home. */
export function getDefaultPostAuthRedirect(): string {
  return PORTAL_HOME_ROUTE;
}

function isSafeReturnPath(value: string): boolean {
  if (!value.startsWith("/") || value.startsWith("//")) {
    return false;
  }
  // Block protocol-relative and off-site schemes smuggled into a path.
  if (value.includes("://")) {
    return false;
  }
  return true;
}

/**
 * Resolve where to send the user after login/signup.
 * Prefer the page they came from (`from` state/query); otherwise portal home.
 * Legacy Markdoc `/api-portal-docs/…` bookmarks map to in-app `#/glofox/…` routes.
 */
export function resolvePostAuthRedirect(
  from: unknown,
  defaultRedirect: string = getDefaultPostAuthRedirect(),
): string {
  if (typeof from !== "string") {
    return defaultRedirect;
  }

  const trimmed = from.trim();
  if (!isSafeReturnPath(trimmed)) {
    return defaultRedirect;
  }

  // Never restore the removed marketing home.
  if (trimmed === "/") {
    return defaultRedirect;
  }

  const fromMarkdoc = markdocPathToSpaRoute(trimmed);
  if (fromMarkdoc) {
    return fromMarkdoc;
  }

  return trimmed;
}

/**
 * True when the return path still points at Markdoc assets (should not happen
 * after `resolvePostAuthRedirect`). Docs browsing uses in-SPA hash routes.
 */
export function isExternalPostAuthRedirect(path: string): boolean {
  if (markdocPathToSpaRoute(path)) {
    return false;
  }
  return path.startsWith("/api-portal-docs/");
}

/** Navigate inside the SPA; convert legacy Markdoc paths when needed. */
export function navigatePostAuth(
  navigate: (to: string) => void,
  path: string,
): void {
  const target = markdocPathToSpaRoute(path) ?? path;
  if (isExternalPostAuthRedirect(target)) {
    window.location.assign(target);
    return;
  }
  navigate(target);
}

export function getRequestAccessAuthNotice(from: unknown): string | null {
  if (typeof from !== "string") {
    return null;
  }
  const path = from.trim();
  return path === GLOFOX_REQUEST_ACCESS_ROUTE ||
    path === GLOFOX_LEGACY_ACCESS_REQUEST_ROUTE
    ? REQUEST_ACCESS_AUTH_NOTICE
    : null;
}

export function readAuthReturnPath(state: unknown): unknown {
  if (!state || typeof state !== "object") {
    return undefined;
  }

  return (state as Record<string, unknown>)[AUTH_RETURN_PATH_STATE_KEY];
}

/** Read `from` from hash/search query (Markdoc deep links into `#/home?from=`). */
export function readAuthReturnPathFromSearch(
  search: string | undefined,
): unknown {
  if (!search) {
    return undefined;
  }
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  return params.get(AUTH_RETURN_PATH_QUERY_KEY) ?? undefined;
}

export function resolveAuthReturnPath(
  state: unknown,
  search?: string,
  defaultRedirect?: string,
): string {
  const fromState = readAuthReturnPath(state);
  if (typeof fromState === "string" && fromState.trim()) {
    return resolvePostAuthRedirect(fromState, defaultRedirect);
  }
  return resolvePostAuthRedirect(
    readAuthReturnPathFromSearch(search),
    defaultRedirect,
  );
}

/** Developer Portal CTA from docs: open `/home` (login). */
export function getDeveloperPortalHref(): string {
  return `/api-portal/#${PORTAL_HOME_ROUTE}`;
}

/** Build `#/home?from=` / `#/home/signup?from=` targets when linking within the SPA. */
export function buildPortalAuthHref(
  route: typeof PORTAL_HOME_ROUTE | typeof PORTAL_SIGNUP_ROUTE | "/login" | "/signup",
  returnPath: string = getDefaultPostAuthRedirect(),
): string {
  const canonical =
    route === "/login" || route === PORTAL_HOME_ROUTE
      ? PORTAL_HOME_ROUTE
      : PORTAL_SIGNUP_ROUTE;
  const safe = resolvePostAuthRedirect(returnPath);
  const query = `${AUTH_RETURN_PATH_QUERY_KEY}=${encodeURIComponent(safe)}`;
  return `/api-portal/#${canonical}?${query}`;
}

/** @deprecated Prefer `getDefaultPostAuthRedirect` / hash routes. */
export function getDocsHomePublicHref(): string {
  return getGlofoxDocsHomeHref();
}
